import { faExclamation as fasExclamation } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { Config } from "@/app/config";
import { AbstractStep } from "@/component/assets/AbstractLeadForm/Steps/AbstractStep";
import type { LeadFormProps, LeadFormStepFields } from "@/component/assets/AbstractLeadForm/Steps/BasicLeadForm";
import { MAX_LOAN_AMOUNT, MIN_LOAN_AMOUNT } from "@/component/PersonalLoan/Calculator/data";
import type { FormIncomeSimpleData } from "@/shared/ui";
import { FormCurrency, FormIncomeSimple, ProgressButton } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";

export interface StepFields extends LeadFormStepFields {
	incomeList?: FormIncomeSimpleData[];
	loanAmount?: number;
}

export interface Props extends LeadFormProps {
	loanAmount?: number;
	incomeList?: FormIncomeSimpleData[];
}

export class IncomePage extends AbstractStep<StepFields, Props> {
	protected initValues(): Partial<StepFields> {
		const { loanAmount, incomeList = [] } = this.props;

		return { ...super.initValues(), loanAmount, incomeList };
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { button = "Adatok mentése", submitState, incomeList = [] } = this.props;

		return (
			<>
				{incomeList.length === 0 && (
					<Card.Header>
						<div className="d-flex">
							<div className="d-flex align-items-center">
								<FontAwesomeIcon
									icon={fasExclamation}
									className="text-gold display-1 mb-0 mr-2"
									fixedWidth
								/>
							</div>
							<div className="h5 mb-0">
								Érdeklődésed véglegesítéséhez kérjük add meg az alábbi adatokat.
							</div>
						</div>
					</Card.Header>
				)}
				<Card.Body>{this.renderFields()}</Card.Body>
				<Card.Footer>
					<div className="text-center">
						<ProgressButton
							variant="gold"
							type="submit"
							size="lg"
							disabled={!form.getState().valid}
							state={submitState}
						>
							{button}
						</ProgressButton>
					</div>
				</Card.Footer>
			</>
		);
	}

	@autobind
	protected renderFields(): ReactNode {
		const { submitState } = this.props;

		return (
			<>
				<FormCurrency
					name="loanAmount"
					label="Hitelösszeg"
					placeholder="Add meg a hitelösszeget"
					validate={this.validateLoanAmount}
					disabled={submitState !== undefined}
					required
				/>
				<FormIncomeSimple
					name="incomeList"
					label="Havi nettó jövedelem"
					apiAddress={Config.getApiUrl("/api/public/bank/loan/personal-loan/income-types")}
					size="sm"
					withReceiptAmount
					withIncomeStart
					disabled={submitState !== undefined}
					buttonVariant="light"
					validate={FormIncomeSimple.validateIncomeList}
					required
				/>
			</>
		);
	}

	@autobind
	private validateLoanAmount(loanAmount: number): string {
		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "IncomePage";
	}
}
